import { db } from "../firebase";
import { doc, updateDoc, Timestamp, getDoc } from "firebase/firestore";
import { FamilyTreeProgress } from "./familyTreeUtils";
import { ORDER_STEPS } from "../constants/orderSteps";
import {
  calculateRequiredImagesForOrder,
  calculateExpectedGenerationsForOrder,
} from "./generationCalculations";

export interface OrderCompletionStatus {
  isComplete: boolean;
  familyTreeComplete: boolean;
  imagesComplete: boolean;
  completionPercentage: number;
  missingRequirements: string[];
}

/**
 * Check if a family tree order is complete based on progress data
 */
export function checkFamilyTreeOrderCompletion(
  progress: FamilyTreeProgress,
  productId: string,
  selectedOptions?: Record<string, any>
): OrderCompletionStatus {
  const requiredSteps = ORDER_STEPS[productId] || [];
  const missingRequirements: string[] = [];

  // Calculate order-specific requirements
  const requiredGenerations = calculateExpectedGenerationsForOrder(
    productId,
    selectedOptions || {}
  );
  const requiredImages = calculateRequiredImagesForOrder(
    productId,
    selectedOptions || {}
  );

  // Check if family tree data is complete for the required generations
  let familyTreeComplete = false;
  if (requiredSteps.includes("FamilyTree")) {
    if (requiredGenerations > 0) {
      // Count how many required generations have complete data
      const completedGenerations = progress.generationProgress
        .slice(0, requiredGenerations)
        .filter((gen) => gen.completionPercentage >= 100).length;

      familyTreeComplete = completedGenerations >= requiredGenerations;

      if (!familyTreeComplete) {
        const missingGens = requiredGenerations - completedGenerations;
        missingRequirements.push(
          `Complete ${missingGens} more generation${
            missingGens !== 1 ? "s" : ""
          }`
        );
      }
    } else {
      // Fallback to overall completion
      familyTreeComplete = progress.overallCompletion >= 100;
      if (!familyTreeComplete) {
        missingRequirements.push("Complete all family member information");
      }
    }
  }

  // Check if required images are uploaded
  let imagesComplete = false;
  if (requiredSteps.includes("Photos")) {
    if (requiredImages > 0) {
      imagesComplete = progress.uploadedImages >= requiredImages;

      if (!imagesComplete) {
        const missingImages = requiredImages - progress.uploadedImages;
        missingRequirements.push(
          `Upload ${missingImages} more image${missingImages !== 1 ? "s" : ""}`
        );
      }
    } else {
      // Fallback to no missing images
      imagesComplete = progress.missingImages === 0;
      if (!imagesComplete) {
        missingRequirements.push(
          `Upload ${progress.missingImages} missing images`
        );
      }
    }
  }

  // Calculate overall completion percentage
  let totalRequirements = 0;
  let completedRequirements = 0;

  if (requiredSteps.includes("FamilyTree")) {
    totalRequirements++;
    if (familyTreeComplete) completedRequirements++;
  }

  if (requiredSteps.includes("Photos")) {
    totalRequirements++;
    if (imagesComplete) completedRequirements++;
  }

  const completionPercentage =
    totalRequirements > 0
      ? Math.round((completedRequirements / totalRequirements) * 100)
      : 100;

  const isComplete =
    familyTreeComplete && imagesComplete && missingRequirements.length === 0;

  return {
    isComplete,
    familyTreeComplete,
    imagesComplete,
    completionPercentage,
    missingRequirements,
  };
}

/**
 * Update order status in Firestore when completion status changes
 */
export interface UpdateOrderResult {
  success: boolean;
  statusChanged: boolean;
  newStatus?: string;
  previousStatus?: string;
}

/**
 * Update order status in Firestore when completion status changes
 */
export async function updateOrderCompletionStatus(
  orderId: string,
  completionStatus: OrderCompletionStatus,
  currentOrderStatus: string,
  progress: FamilyTreeProgress,
  existingOrderData?: any
): Promise<UpdateOrderResult> {
  try {
    const orderRef = doc(db, "orders", orderId);
    const updates: any = {};
    let statusChanged = false;
    let newStatus = currentOrderStatus;

    // If order is now complete and wasn't before
    // We only move to 'in-progress' if it was 'pending' or 'awaiting-info'
    if (
      completionStatus.isComplete &&
      currentOrderStatus !== "in-progress" &&
      currentOrderStatus !== "completed" &&
      currentOrderStatus !== "shipped" &&
      currentOrderStatus !== "cancelled"
    ) {
      updates.status = "in-progress";
      updates.actionsRequired = []; // Clear any pending actions
      updates.lastFormUpdate = Timestamp.now();
      
      // Add status change to history
      updates.statusHistory = [
        ...(existingOrderData?.statusHistory || []),
        {
          status: "in-progress",
          timestamp: Timestamp.now(),
          reason: "All requirements completed - ready for production",
        }
      ];
      
      statusChanged = true;
      newStatus = "in-progress";
    }
    // If order was complete but is no longer (user made changes)
    // Only revert if it was 'in-progress'
    else if (
      !completionStatus.isComplete &&
      currentOrderStatus === "in-progress"
    ) {
      updates.status = "awaiting-info";
      updates.lastFormUpdate = Timestamp.now();
      updates.actionsRequired = completionStatus.missingRequirements.map(
        (req) => {
          if (req.includes("family member")) return "FamilyTree";
          if (req.includes("images")) return "Photos";
          return "FamilyTree"; // Default
        }
      );

      // Add status change to history
      updates.statusHistory = [
        ...(existingOrderData?.statusHistory || []), 
        {
          status: "awaiting-info",
          timestamp: Timestamp.now(),
          reason: "Requirements no longer met",
        }
      ];
      
      statusChanged = true;
      newStatus = "awaiting-info";
    }

    // Always update completion percentage and lastFormUpdate if strictly necessary
    // Only update timestamp if we didn't change status but progress changed
    if (!statusChanged) {
        // Check if percentage changed or meaningful progress changed effectively
        // For now, we'll be less aggressive with lastFormUpdate to avoid spamming writes
        // on every keystroke if nothing meaningful changed.
        // We'll update it if percentage changed.
        if (existingOrderData?.completionPercentage !== completionStatus.completionPercentage) {
            updates.lastFormUpdate = Timestamp.now();
        }
    }

    updates.completionPercentage = completionStatus.completionPercentage;

    // Update progress tracking fields for order cards
    if (progress) {
      // Use existing order data if provided, otherwise fetch (backward compatibility/fallback)
      let orderData = existingOrderData;
      if (!orderData) {
        const orderDoc = await getDoc(orderRef);
        orderData = orderDoc.data();
      }

      const productId = orderData?.items?.[0]?.productId;
      const selectedOptions = orderData?.items?.[0]?.selectedOptions || {};

      // Calculate order-specific requirements
      const requiredGenerations = calculateExpectedGenerationsForOrder(
        productId,
        selectedOptions
      );
      const requiredImages = calculateRequiredImagesForOrder(
        productId,
        selectedOptions
      );

      // Calculate generation progress based on order requirements
      const relevantGenerations =
        requiredGenerations > 0
          ? progress.generationProgress.slice(0, requiredGenerations)
          : progress.generationProgress;

      const completedGenerations = relevantGenerations.filter(
        (gen) => gen.completionPercentage >= 100
      ).length;

      updates.generationProgress = {
        totalGenerations:
          requiredGenerations > 0
            ? requiredGenerations
            : progress.generationProgress.length,
        completedGenerations,
      };

      // Update image progress with order-specific requirements
      const totalImages =
        requiredImages > 0
          ? requiredImages
          : Math.max(0, progress.totalImages || 0);
      const uploadedImages = Math.max(
        0,
        Math.min(progress.uploadedImages || 0, totalImages)
      );

      updates.imageProgress = {
        totalImages,
        uploadedImages,
      };
    }

    // Only perform write if there are actual updates
    // (Optimization: compare updates against existingData if possible, 
    // but for now relying on logic above is safer than naive deep compare)
    // We definitely write if statusChanged or percentage changed.
    // If only deep progress object changed, we should also write.
    // To be safe, we write, but the timestamp is less aggressive now.
    
    if (Object.keys(updates).length > 0) {
        await updateDoc(orderRef, updates);
    }
    
    return {
        success: true,
        statusChanged,
        newStatus,
        previousStatus: currentOrderStatus
    };

  } catch (error) {
    console.error("Error updating order completion status:", error);
    return {
        success: false,
        statusChanged: false
    };
  }
}

/**
 * Get user-friendly status message for order completion
 */
export function getOrderCompletionMessage(
  completionStatus: OrderCompletionStatus
): string {
  if (completionStatus.isComplete) {
    return "🎉 Your order is complete and ready for processing!";
  }

  if (completionStatus.missingRequirements.length === 0) {
    return "Almost there! Just a few more details needed.";
  }

  const requirements = completionStatus.missingRequirements.join(" and ");
  return `To complete your order: ${requirements}`;
}

/**
 * Check if we should show completion celebration
 */
export function shouldShowCompletionCelebration(
  previousStatus: OrderCompletionStatus | null,
  currentStatus: OrderCompletionStatus
): boolean {
  return (
    currentStatus.isComplete && (!previousStatus || !previousStatus.isComplete)
  );
}
